import axios from 'axios';
import { dropboxBaseURL } from '../constants/ApiEndPoints';

const BASE_URL = dropboxBaseURL;

/**
 * Dropbox API Service
 * Handles all API calls for the Dropbox file management system
 */

// ============================================================================
// FOLDER OPERATIONS
// ============================================================================

export const getFolders = async (params?: {
  parent_id?: string;
  include_deleted?: boolean;
  global_only?: boolean;
  personal_only?: boolean;
}) => {
  try {
    const response = await axios.get(`${BASE_URL}/folders`, { params });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const getFolderById = async (id: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/folders/${id}`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const createFolder = async (data: { name: string; parent_id?: string | null; is_global?: boolean }) => {
  try {
    const response = await axios.post(`${BASE_URL}/folders`, data);
    if (response.status === 201) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const updateFolder = async (id: string, data: { name: string }) => {
  try {
    const response = await axios.put(`${BASE_URL}/folders/${id}`, data);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const deleteFolder = async (id: string, permanent: boolean = false) => {
  try {
    const response = await axios.delete(`${BASE_URL}/folders/${id}${permanent ? '?permanent=true' : ''}`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const restoreFolder = async (id: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/folders/${id}/restore`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const moveFolder = async (id: string, new_parent_id: string | null) => {
  try {
    const response = await axios.put(`${BASE_URL}/folders/${id}/move`, { new_parent_id });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const reorderFolder = async (id: string, sort_order: number) => {
  try {
    const response = await axios.put(`${BASE_URL}/folders/${id}/reorder`, { sort_order });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const copyFolder = async (id: string, new_parent_id?: string | null, new_name?: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/folders/${id}/copy`, { new_parent_id, new_name });
    if (response.status === 201 || response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

// ============================================================================
// FILE OPERATIONS
// ============================================================================

export const getFiles = async (params?: {
  folder_id?: string;
  include_deleted?: boolean;
  starred_only?: boolean;
  global_only?: boolean;
  personal_only?: boolean;
}) => {
  try {
    const response = await axios.get(`${BASE_URL}/files`, { params });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const getFileById = async (id: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/files/${id}`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const uploadFile = async (formData: FormData, onUploadProgress?: (progressEvent: any) => void) => {
  try {
    const response = await axios.post(`${BASE_URL}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    if (response.status === 201) {
      return response.data;
    } else {
      // Handle unexpected success status codes
      return {
        success: false,
        message: `Unexpected response status: ${response.status}`
      };
    }
  } catch (error) {
    return error?.response?.data || {
      success: false,
      message: error?.message || 'Network error occurred'
    };
  }
};

export const updateFile = async (id: string, data: { name: string }) => {
  try {
    const response = await axios.put(`${BASE_URL}/files/${id}`, data);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const deleteFile = async (id: string, permanent: boolean = false) => {
  try {
    const response = await axios.delete(`${BASE_URL}/files/${id}${permanent ? '?permanent=true' : ''}`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const restoreFile = async (id: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/files/${id}/restore`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const downloadFile = async (id: string, version?: number) => {
  try {
    const response = await axios.get(`${BASE_URL}/files/${id}/download`, {
      params: { version },
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const previewFile = async (id: string, version?: number) => {
  try {
    // First try a dedicated preview endpoint
    const response = await axios.get(`${BASE_URL}/files/${id}/preview`, {
      params: { version },
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    // If preview endpoint doesn't exist, try download with inline parameter
    try {
      const fallbackResponse = await axios.get(`${BASE_URL}/files/${id}/download`, {
        params: { 
          version, 
          inline: true,
          disposition: 'inline' // Request inline content disposition
        },
      });
      if (fallbackResponse.status === 200) {
        return fallbackResponse.data;
      }
    } catch (fallbackError) {
      return fallbackError?.response?.data;
    }
    return error?.response?.data;
  }
};

export const streamFile = async (id: string, version?: number) => {
  try {
    const response = await axios.get(`${BASE_URL}/files/${id}/stream`, {
      params: { version },
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const starFile = async (id: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/files/${id}/star`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const unstarFile = async (id: string) => {
  try {
    const response = await axios.delete(`${BASE_URL}/files/${id}/star`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const moveFile = async (id: string, new_folder_id: string | null) => {
  try {
    const response = await axios.put(`${BASE_URL}/files/${id}/move`, { new_folder_id });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const reorderFile = async (id: string, sort_order: number) => {
  try {
    const response = await axios.put(`${BASE_URL}/files/${id}/reorder`, { sort_order });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const copyFile = async (id: string, new_folder_id?: string | null, new_name?: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/files/${id}/copy`, { new_folder_id, new_name });
    if (response.status === 201 || response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

// ============================================================================
// TAG OPERATIONS
// ============================================================================

export const addTags = async (fileId: string, tags: string[]) => {
  try {
    const response = await axios.post(`${BASE_URL}/files/${fileId}/tags`, { tags });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const removeTag = async (fileId: string, tag: string) => {
  try {
    const response = await axios.delete(`${BASE_URL}/files/${fileId}/tags/${encodeURIComponent(tag)}`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const getFileTags = async (fileId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/files/${fileId}/tags`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const getAllTags = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/tags`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

// ============================================================================
// RECENT ACTIVITY
// ============================================================================

export const getRecentActivity = async (params?: { limit?: number; entity_type?: 'file' | 'folder' }) => {
  try {
    const response = await axios.get(`${BASE_URL}/recent`, { params });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const trackFileAccess = async (fileId: string, action: string = 'opened') => {
  try {
    const response = await axios.post(`${BASE_URL}/files/${fileId}/track-access`, { action });
    if (response.status === 200 || response.status === 201) {
      return response.data;
    }
    return { success: false, message: `Unexpected status: ${response.status}` };
  } catch (error) {
    console.error('Track file access error:', error);
    return error?.response?.data || { success: false, message: 'Failed to track file access' };
  }
};

export const trackFolderAccess = async (folderId: string, action: string = 'opened') => {
  try {
    const response = await axios.post(`${BASE_URL}/folders/${folderId}/track-access`, { action });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    // Fail silently for tracking
    return null;
  }
};

export const clearRecentActivity = async () => {
  try {
    const response = await axios.delete(`${BASE_URL}/recent`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

// ============================================================================
// SEARCH
// ============================================================================

export const searchFiles = async (params: {
  query?: string;
  folder_id?: string;
  file_type?: string;
  tags?: string[];
  starred_only?: boolean;
}) => {
  try {
    const response = await axios.get(`${BASE_URL}/search`, { 
      params: {
        q: params.query,
        ...params
      }
    });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

// ============================================================================
// SHARE LINKS
// ============================================================================

export const createShareLink = async (data: {
  entity_type: 'file' | 'folder';
  entity_id: string;
  expires_at?: string;
  max_downloads?: number;
  password?: string;
}) => {
  try {
    const response = await axios.post(`${BASE_URL}/share-links`, data);
    if (response.status === 201) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const getShareLinks = async (params?: { entity_type?: string; entity_id?: string }) => {
  try {
    const response = await axios.get(`${BASE_URL}/share-links`, { params });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const deleteShareLink = async (id: string) => {
  try {
    const response = await axios.delete(`${BASE_URL}/share-links/${id}`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

// ============================================================================
// STORAGE
// ============================================================================

export const getStorageUsage = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/storage/usage`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

// ============================================================================
// FILE VERSIONS
// ============================================================================

export const getFileVersions = async (fileId: string) => {
  try {
    const response = await axios.get(`${BASE_URL}/files/${fileId}/versions`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const restoreFileVersion = async (fileId: string, versionId: string) => {
  try {
    const response = await axios.post(`${BASE_URL}/files/${fileId}/versions/${versionId}/restore`);
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

// ============================================================================
// BULK OPERATIONS
// ============================================================================

export const bulkMove = async (items: Array<{ id: string; type: 'file' | 'folder' }>, target_folder_id: string | null) => {
  try {
    const response = await axios.post(`${BASE_URL}/bulk/move`, { items, target_folder_id });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const bulkCopy = async (items: Array<{ id: string; type: 'file' | 'folder' }>, target_folder_id: string | null) => {
  try {
    const response = await axios.post(`${BASE_URL}/bulk/copy`, { items, target_folder_id });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const bulkDelete = async (items: Array<{ id: string; type: 'file' | 'folder' }>, permanent: boolean = false) => {
  try {
    const response = await axios.post(`${BASE_URL}/bulk/delete`, { items, permanent });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const bulkStar = async (file_ids: string[]) => {
  try {
    const response = await axios.post(`${BASE_URL}/bulk/star`, { file_ids });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const bulkUnstar = async (file_ids: string[]) => {
  try {
    // Since there's no bulk unstar endpoint, we'll handle this in the frontend
    // by calling unstar for each file individually
    const results = {
      success: true,
      successCount: 0,
      failCount: 0,
      errors: [] as any[]
    };

    for (const fileId of file_ids) {
      try {
        const response = await unstarFile(fileId);
        if (response?.success) {
          results.successCount++;
        } else {
          results.failCount++;
          results.errors.push({ fileId, error: response?.message || 'Failed' });
        }
      } catch (error: any) {
        results.failCount++;
        results.errors.push({ fileId, error: error?.message || 'Failed to unstar' });
      }
    }

    return {
      success: results.failCount === 0,
      message: `${results.successCount} file(s) removed from favourites`,
      data: results
    };
  } catch (error) {
    return error?.response?.data;
  }
};

export const bulkDownload = async (items: Array<{ id: string; type: 'file' | 'folder' }>) => {
  try {
    const response = await axios.post(`${BASE_URL}/bulk/download`, { items });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

// ============================================================================
// SORT ORDER OPERATIONS
// ============================================================================

export const updateFileSortOrder = async (id: string, sort_order: number) => {
  try {
    const response = await axios.put(`${BASE_URL}/files/${id}/sort-order`, { sort_order });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const updateFolderSortOrder = async (id: string, sort_order: number) => {
  try {
    const response = await axios.put(`${BASE_URL}/folders/${id}/sort-order`, { sort_order });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const bulkUpdateSortOrder = async (items: Array<{ id: string; type: 'file' | 'folder'; sort_order: number }>) => {
  try {
    const response = await axios.post(`${BASE_URL}/bulk/update-sort-order`, { items });
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return error?.response?.data;
  }
};

export const bulkRestore = async (items: Array<{ id: string; type: 'file' | 'folder' }>) => {
  try {
    const results = {
      restored: 0,
      failed: 0,
      errors: [] as any[]
    };

    for (const item of items) {
      try {
        if (item.type === 'file') {
          const response = await restoreFile(item.id);
          
          if (response?.success) {
            results.restored++;
          } else {
            results.failed++;
            results.errors.push({ id: item.id, type: 'file', error: response?.message || 'Failed' });
          }
        } else if (item.type === 'folder') {
          const response = await restoreFolder(item.id);
          
          if (response?.success) {
            results.restored++;
          } else {
            results.failed++;
            results.errors.push({ id: item.id, type: 'folder', error: response?.message || 'Failed' });
          }
        }
      } catch (error: any) {
        results.failed++;
        results.errors.push({ id: item.id, type: item.type, error: error?.message || 'Failed to restore' });
      }
    }

    return {
      success: true,
      message: `${results.restored} item(s) restored successfully`,
      data: results
    };
  } catch (error) {
    return error?.response?.data;
  }
};
